import modalActions from "actions/modal";
import cn from "classnames";
import { Transition } from "components/shared/column";
import InfoIcon from "components/shared/iconInfo";
import Loader from "components/shared/loader";
import { formatNumber, ymEvent } from "helpers/system";
import usePagination from "hooks/usePagination";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { download, getItem } from "sources/books/reports";
import Form from "./form";
import SelectPeriod from "./selectPeriod";
import Show from "./show";

const Table = (props) => {
    const { modal, data } = props;

    const { page, goToPage, pagesCount, visiblePages } = usePagination(data);

    const handleShowDriverSalary = (driverSalary) => {
        return modal({
            name: "default",
            windowMod: "wide",
            CustomBody: Show,
            bodyData: { driverSalary },
        });
    };

    return (
        <table className="table table-sm table-striped mb-0">
            <thead>
                <tr className="bg-ff-2c" style={{ position: "sticky", top: -1 }}>
                    <th>#</th>
                    <th>Водитель</th>
                    <th>Сумма к выплате</th>
                </tr>
            </thead>
            <tbody>
                {!page.items?.length ? (
                    <tr>
                        <td colSpan={3} className="text-center">
                            Нет данных
                        </td>
                    </tr>
                ) : (
                    <>
                        {page.items.map((driverSalary, index) => (
                            <tr key={index}>
                                <td>{index + 1}</td>
                                <td>
                                    <span className="link" onClick={() => handleShowDriverSalary(driverSalary)}>
                                        {driverSalary.driverName}
                                    </span>
                                </td>
                                <td>
                                    {formatNumber(driverSalary.totalSalary, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </td>
                            </tr>
                        ))}
                    </>
                )}
            </tbody>
            <tfoot className="bg-ff-2c" style={{ position: "sticky", bottom: 0 }}>
                <tr>
                    <td colSpan={3}>
                        <div className="pagination__pages">
                            {pagesCount > 6 && page.pageIndex > 3 && (
                                <div className="pagination__item" onClick={() => goToPage(0)}>
                                    &laquo;
                                </div>
                            )}
                            {visiblePages.map((pageIndex) => {
                                return (
                                    <div
                                        key={pageIndex}
                                        className={cn("pagination__item", {
                                            "pagination__item--active": page.pageIndex === pageIndex - 1,
                                        })}
                                        onClick={goToPage.bind(this, pageIndex - 1)}
                                    >
                                        {pageIndex}
                                    </div>
                                );
                            })}
                            {pagesCount > 6 && page.pageIndex < pagesCount - 3 && (
                                <div className="pagination__item" onClick={() => goToPage(pagesCount - 1)}>
                                    &raquo;
                                </div>
                            )}
                        </div>
                    </td>
                </tr>
            </tfoot>
        </table>
    );
};

const CalculationDriverSalary = (props) => {
    const { title, modal } = props;

    const [isLoading, setIsLoading] = useState(true);
    const [needUpdate, setNeedUpdate] = useState(false);
    const [openedDropd, setOpenedDropd] = useState(false);
    const [data, setData] = useState([]);
    const [period, setPeriod] = useState({
        year: moment().year(),
        month: moment().format("MM"),
    });

    useEffect(() => {
        ymEvent("DriverSalaryOpen");

        getList();
    }, []);

    useEffect(() => {
        if (needUpdate) {
            getList();

            setNeedUpdate(false);
        }
    }, [needUpdate]);

    // преобразует { year: 2025, month: "07" } в "июль 2025 г."
    const periodTextFormatted = useMemo(() => {
        return new Intl.DateTimeFormat("ru-RU", { month: "long", year: "numeric" }).format(
            new Date(Number(period.year), Number(period.month - 1)),
        );
    }, [period]);

    const refetch = () => setNeedUpdate(true);

    const getList = async () => {
        setIsLoading(true);

        const response = await getItem({
            id: "DriverSalaryReport",
            reportPeriod: `${period.year}-${period.month}-01T00:00:00.00Z`,
        });

        if (response) {
            setData(response.data);
        }

        setIsLoading(false);
    };

    const handleShowSalarySettingsForm = () => {
        if (isLoading) {
            return;
        }

        ymEvent("SetUpSalary");

        return props.modal({
            name: "default",
            title: `Настройка параметров расчета ЗП за ${periodTextFormatted}`,
            windowMod: "wide",
            CustomBody: Form,
            bodyData: { period, refetch },
        });
    };

    const downloadExcel = () => {
        ymEvent("DriverSalaryExcel");

        setIsLoading(true);

        const params = {
            id: "DriverSalaryReport",
            fileName: `Расчёт зарплаты водителей за ${periodTextFormatted}xlsx`,
            reportPeriod: `${period.year}-${period.month}-01T00:00:00.00Z`,
        };

        download(params).then(() => setIsLoading(false));
    };

    const dropdActions = () => {
        const actions = [{ text: "Скачать", action: downloadExcel }];
        return actions;
    };

    const renderDropd = () => {
        return (
            openedDropd && (
                <ul className="c-dropd__list no-bullet">
                    {dropdActions().map((item, i) => (
                        <li key={i} className="c-dropd__list-item" onClick={item.action}>
                            {item.text}
                        </li>
                    ))}
                </ul>
            )
        );
    };

    const openDropd = () => {
        if (openedDropd) {
            return;
        }
        setOpenedDropd(true);
        setTimeout(() => document.addEventListener("click", closeDropd), 0);
    };

    const closeDropd = () => {
        setOpenedDropd(false);
        document.removeEventListener("click", closeDropd);
    };

    return (
        <Transition dir="full">
            <div className="page-book__show book-show">
                <div className="book-show__head">
                    <div className="book-show__close close close--sm" onClick={props.back} />
                    <div className="book-show__title">
                        {title}&nbsp;
                        <InfoIcon
                            placement="right"
                            text={`Данные за предыдущий месяц в отчете сохраняются каждого 7-го числа месяца и не изменяются при корректировке архивных данных`}
                        />
                    </div>
                    {!!dropdActions().length && (
                        <div className="book-show__dropdown c-dropd">
                            <div className="c-dropd__btn" onClick={openDropd} />
                            {renderDropd()}
                        </div>
                    )}
                </div>
                <div className="book-show__body">
                    <div className="d-flex flex-column h-100 position-relative">
                        <div className="d-flex gap-2 mb-3 flex-wrap">
                            <button className="btn btn-sm btn-brand" onClick={handleShowSalarySettingsForm}>
                                Настроить параметры расчета ЗП
                            </button>
                            <SelectPeriod period={period} setPeriod={setPeriod} refetch={refetch} />
                        </div>
                        <div className="flex-1 mb-3 overflow-auto" style={{ maxWidth: "643px" }}>
                            {isLoading ? (
                                <div className="position-relative loader-wrap">
                                    <Loader prefix="absolute-center" />
                                </div>
                            ) : (
                                <Table modal={modal} data={data} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    );
};

const container = (Component) => {
    const mapState = (_state) => ({});

    const mapDispatch = (dispatch) => ({
        modal: (params) => dispatch(modalActions.open(params)),
    });

    return connect(mapState, mapDispatch)(Component);
};

export default container(CalculationDriverSalary);
